import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { useRouter } from 'next/router';
import StudioWrapper from '../StudioWrapper';
import Button from '@components/Shared/Buttons/Button';
import useWindowResize from 'pubweb-smokey/dist/hooks/useWindowResize';
import * as variables from 'pubweb-smokey/dist/components/GridSystem/_vars_widths.js';
import PostCard from '../PostCard/PostCard';
import PostCTA from '../PostCTA/PostCTA';
import EmailWebForm from '../EmailWebForm/EmailWebForm';
import ScrollUpButtonContainer from '../../Shared/ScrollUpButton/ScrollUpButtonContainer';
import PreFooterCTA from '../PreFooterCTA/PreFooterCTA';
import CategoryPageStyles from './Category.styled';
import ClaytonLogo from '@components/Shared/ClaytonLogo/ClaytonLogo';
import { getSiteUrl } from '@utils/config';

const CARDS_PER_LOAD = 12;
const DESKTOP_CTA_FREQUENCY = 10;
const MOBILE_CTA_FREQUENCY = 6;

const Category = ({
  pageInfo,
  summary,
  posts,
  currentCategory,
  curatedButtons,
}) => {
  const buildCards = (postList) => {
    const cards = [];
    let ctaIndex = 0;

    for (let i = 0; i < postList.length; i++) {
      if (i > 0 && i % ctaFreqency == 0) {
        const cta = pageInfo?.contentCTAs[ctaIndex++];

        if (ctaIndex >= pageInfo?.contentCTAs.length) {
          ctaIndex = 0;
        }

        cards.push(
          <PostCTA
            key={`post-cta-${currentCategory.categoryKey}-${i}`}
            cta={cta}
          />
        );
      }

      cards.push(
        <PostCard
          key={`post-card-${currentCategory.categoryKey}-${i}`}
          post={postList[i]}
        />
      );
    }

    return cards;
  };

  const [ctaFreqency, setCtaFrequency] = useState(DESKTOP_CTA_FREQUENCY);
  const [displayLimit, setDisplayLimit] = useState(CARDS_PER_LOAD);
  const [cards, setCards] = useState(buildCards(posts));
  const router = useRouter();

  const getOgImage = () => {
    if (pageInfo && pageInfo.ogImage) {
      return pageInfo.ogImage.file.url;
    }

    return `/studio/${ClaytonLogo}`;
  };

  useEffect(() => {
    setCards(buildCards(posts));
  }, [ctaFreqency, currentCategory]);

  useWindowResize(() => {
    if (window.innerWidth >= variables.desktop_breakpoint_extended) {
      setCtaFrequency(DESKTOP_CTA_FREQUENCY);
    } else {
      setCtaFrequency(MOBILE_CTA_FREQUENCY);
    }
  }, [variables.desktop_breakpoint_extended]);

  return (
    <StudioWrapper
      key={currentCategory.slug}
      posts={summary.allPosts}
      heroData={pageInfo}
      heroHeadline={currentCategory.categoryName}
      curatedButtons={curatedButtons}
    >
      <Head>
        <title>{pageInfo?.titleTag}</title>
        <meta name="description" content={pageInfo?.metaDescription} />
        <meta property="og:image" content={getOgImage()} />
        <link
          rel="canonical"
          href={`${getSiteUrl(
            router.asPath.endsWith('/') ? router.asPath : router.asPath + '/'
          )}`}
        />
      </Head>

      <ScrollUpButtonContainer isStudio={true} />

      <CategoryPageStyles>
        <div className="constrained-width">
          <div className="posts">{cards.slice(0, displayLimit)}</div>

          {displayLimit < cards.length && (
            <div className="load-more">
              <Button
                className="gtm-studio-load-more-button"
                isCompact={true}
                buttonStyle="outlined"
                onClick={() => {
                  setDisplayLimit(displayLimit + CARDS_PER_LOAD);
                }}
              >
                Load More
              </Button>
            </div>
          )}
        </div>

        {pageInfo?.webForms[0] && (
          <EmailWebForm
            webFormData={pageInfo.webForms[0]}
            pageType="studio_category"
          />
        )}

        {pageInfo?.preFooterCTA && (
          <PreFooterCTA preFooterCTA={pageInfo.preFooterCTA} />
        )}
      </CategoryPageStyles>
    </StudioWrapper>
  );
};

Category.propTypes = {
  curatedButtons: PropTypes.any,
  currentCategory: PropTypes.shape({
    categoryName: PropTypes.any,
    slug: PropTypes.string,
    categoryKey: PropTypes.string,
  }),
  pageInfo: PropTypes.shape({
    contentCTAs: PropTypes.array,
    heroCTA: PropTypes.any,
    heroImage: PropTypes.any,
    metaDescription: PropTypes.string,
    mobileHeroImage: PropTypes.any,
    ogImage: PropTypes.shape({
      file: PropTypes.shape({
        url: PropTypes.any,
      }),
    }),
    pageTitle: PropTypes.any,
    preFooterCTA: PropTypes.any,
    titleTag: PropTypes.string,
    webForms: PropTypes.any,
  }),
  posts: PropTypes.any,
  summary: PropTypes.shape({
    allPosts: PropTypes.any,
  }),
};

export default Category;
